import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Notification from './components/utils_jsx/Notification';
import Home from './components/Home';
import DemoSelection from './components/DemoSelection';
import SignUp from './components/SignUp';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Documentation from './components/Documentation'; // ← à créer
import Vision from './components/Vision'; // ← à créer
import FAQ from './components/FAQ'; // ← à créer
import PolitiqueConfidentialite from './components/PolitiqueConfidentialite';  
import './index.css';
import { GoogleOAuthProvider } from '@react-oauth/google';

function App() {

  const [showMobileWarning, setShowMobileWarning] = useState(false);

  useEffect(() => {
    // Détection mobile simple et fiable
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || window.innerWidth <= 768;

    if (isMobile) {
      // On affiche seulement si pas déjà vu (localStorage)
      const dismissed = localStorage.getItem('mobileWarningDismissed');
      if (!dismissed) {
        setShowMobileWarning(true);
      }
    }
  }, []);

  const dismissWarning = () => {
    localStorage.setItem('mobileWarningDismissed', 'true');
    setShowMobileWarning(false);
  };

  const dismissWarningPlusVision = () => {
    localStorage.setItem('mobileWarningDismissed', 'true');
    setShowMobileWarning(false);
    window.open('/vision', '_blank')
  };

  const [notification, setNotification] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  // isDemo : stocké séparément, persiste après refresh
  const [isDemo, setIsDemo] = useState(() => {
    const storedDemo = localStorage.getItem('isDemoMode');
    return storedDemo === 'true';
  });
  const [demoID, setDemoID] = useState(null);
  const [monthUpdate, setMonthUpdate] = useState(false);

  // Quand on change isDemo, on le persiste
  useEffect(() => {
    localStorage.setItem('isDemoMode', isDemo.toString());
  }, [isDemo]);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-900 text-white'} w-full transition-colors duration-300 relative`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">

        {/* Notification toast classique */}
        {notification && <Notification message={notification.message} type={notification.type} />}

        {/* ─── NOUVEAU : Bandeau fixe en haut sur mobile ─────────────── */}
        {showMobileWarning && (
          <div className="fixed top-0 left-0 right-0 bg-amber-600/90 text-white text-center py-2.5 z-50 text-sm font-medium shadow-md">
            Optimisé pour ordinateur • L'expérience peut être limitée sur mobile
          </div>
        )}

        <div className="w-full">

          {/* ─── NOUVEAU : Overlay modal au centre quand warning actif ─── */}
          {showMobileWarning && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 max-w-lg w-full text-center shadow-2xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold mb-5 text-gray-900 dark:text-white">
                  Site optimisé pour ordinateur
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                  Ce tableau de bord est conçu pour une utilisation confortable sur ordinateur ou tablette.<br />
                  Sur téléphone, certains graphiques, tableaux et interactions risquent d'être difficiles à utiliser.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={dismissWarning}
                    className="px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition shadow-md"
                  >
                    Continuer quand même
                  </button>
                  <button
                    onClick={dismissWarningPlusVision}
                    className="px-8 py-3.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-xl transition"
                  >
                    Lire notre vision
                  </button>
                </div>
              </div>
            </div>
          )}

          <Routes>
            <Route path="/" element={
              <Home 
                theme={theme}
                toggleTheme={toggleTheme}
                setIsDemo={setIsDemo}
                showNotification={showNotification}
              />} 
            />
            <Route path="/demo-selection" element={
              <DemoSelection 
                theme={theme}
                toggleTheme={toggleTheme}
                setIsDemo={setIsDemo}
                setDemoID={setDemoID}
                demoID={demoID}
                isDemo={isDemo}
              />} 
            />
            <Route path="/documentation" element={
              <Documentation theme={theme} toggleTheme={toggleTheme} showNotification={showNotification} />} 
            />

            <Route path="/vision" element={<Vision theme={theme} />} />

            <Route path="/politique-confidentialite" element={
              <PolitiqueConfidentialite theme={theme} toggleTheme={toggleTheme} />} 
            />
            
            <Route path="/faq" element={<FAQ theme={theme} toggleTheme={toggleTheme} />} />
            {/* ContentRoutes retourne maintenant un seul <Route> valide */}
            <Route
              path="/dashboard/*"  element={
              <Dashboard 
                showNotification={showNotification}
                theme={theme}
                toggleTheme={toggleTheme}
                isDemo={isDemo}
                demoID={demoID}
                monthUpdate={monthUpdate}
                setMonthUpdate={setMonthUpdate}
              />}
            />
          </Routes>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
            <Routes>
              <Route path="/signup" element={<SignUp theme={theme} showNotification={showNotification} setIsDemo={setIsDemo} />} />
              <Route path="/login" element={<Login theme={theme} showNotification={showNotification} setToken={setToken} setIsDemo={setIsDemo} />} />
            </Routes>
          </GoogleOAuthProvider>
        </div>
      </div>
    </div>
  );
}

export default App;